import type { WsEvent } from '@chatapp-inbox/shared';
import { APP_NAME } from '../../app/brand';
import { readSoundEnabled } from './sound-preference';

type AudioContextCtor = typeof AudioContext;

/** Título da aba quando não há nada pendente. */
export const APP_TITLE = APP_NAME;

/** `undefined` = conversa desconhecida; `null` = sem responsável; senão o id do responsável. */
export type AssigneeLookup = (conversationId: string) => string | null | undefined;

export interface NotifierOptions {
  viewerId: string;
  /** Id da conversa aberta na tela agora (ou null). */
  openConversationId: () => string | null;
  assigneeOf: AssigneeLookup;
  /** Para testes. */
  playSound?: () => void;
  soundEnabled?: () => boolean;
}

/**
 * Chama a atenção para mensagens RECEBIDAS quando a pessoa não as está vendo: título da aba com o
 * número de pendentes (só com a aba escondida/sem foco) e um som curto (se ligado). Só conta
 * conversas que são dela ou ainda sem responsável: a conversa de um colega não é problema dela.
 */
export class AttentionNotifier {
  private pending = 0;
  private baseTitle: string | null = null;
  private audio: AudioContext | null = null;
  private readonly unlock = (): void => {
    this.ensureAudio();
  };

  constructor(private readonly options: NotifierOptions) {
    document.addEventListener('visibilitychange', this.onAttention);
    window.addEventListener('focus', this.onAttention);
    // Navegadores só deixam tocar áudio depois de um gesto da pessoa: o primeiro toque/tecla
    // prepara o contexto de áudio para os sons seguintes.
    window.addEventListener('pointerdown', this.unlock, { once: true });
    window.addEventListener('keydown', this.unlock, { once: true });
  }

  dispose(): void {
    document.removeEventListener('visibilitychange', this.onAttention);
    window.removeEventListener('focus', this.onAttention);
    window.removeEventListener('pointerdown', this.unlock);
    window.removeEventListener('keydown', this.unlock);
    this.clearTitle();
    void this.audio?.close().catch(() => undefined);
    this.audio = null;
  }

  handle(event: WsEvent): void {
    if (event.type !== 'message.created' || event.message.direction !== 'in') return;
    const { conversationId } = event.message;
    const assignee = this.options.assigneeOf(conversationId);
    const mine = assignee === this.options.viewerId;
    if (assignee !== undefined && assignee !== null && !mine) return;

    const watching =
      this.options.openConversationId() === conversationId &&
      document.visibilityState === 'visible' &&
      document.hasFocus();
    if (watching) return;

    if ((this.options.soundEnabled ?? readSoundEnabled)()) this.beep();
    if (document.visibilityState === 'hidden' || !document.hasFocus()) {
      this.pending += 1;
      this.baseTitle ??= document.title;
      document.title = `(${this.pending}) ${this.baseTitle}`;
    }
  }

  /** A pessoa voltou à aba: o título volta ao normal. */
  private readonly onAttention = (): void => {
    if (document.visibilityState === 'visible' && document.hasFocus()) this.clearTitle();
  };

  private clearTitle(): void {
    if (this.baseTitle !== null) document.title = this.baseTitle;
    this.baseTitle = null;
    this.pending = 0;
  }

  // ------------------------------------------------------------------ som

  private ensureAudio(): AudioContext | null {
    if (this.audio) return this.audio;
    const Ctor: AudioContextCtor | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.audio = new Ctor();
    } catch {
      return null;
    }
    return this.audio;
  }

  private beep(): void {
    if (this.options.playSound) {
      this.options.playSound();
      return;
    }
    const audio = this.ensureAudio();
    if (!audio) return;
    try {
      void audio.resume?.();
      // Duas notas curtas e baixas (nada de susto): sol -> dó.
      const start = audio.currentTime;
      for (const [offset, frequency] of [
        [0, 880],
        [0.14, 660],
      ] as const) {
        const oscillator = audio.createOscillator();
        const gain = audio.createGain();
        oscillator.frequency.value = frequency;
        gain.gain.setValueAtTime(0.0001, start + offset);
        gain.gain.exponentialRampToValueAtTime(0.06, start + offset + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.13);
        oscillator.connect(gain).connect(audio.destination);
        oscillator.start(start + offset);
        oscillator.stop(start + offset + 0.14);
      }
    } catch {
      /* sem áudio: segue só com o título */
    }
  }
}
